/* eslint-disable react/jsx-handler-names */
import { ReoButton, ReoIcon, ReoPopup, ReoSelect } from '@reolink-web/components.basic';
import React from 'react';
import styles from './index.module.less';
import type { ISupportLangItem, ISupportCountryItem } from '@/common';
import { COUNTRY_COOKIE_KEY, langCodeTransform, LANGUAGE_COOKIE_KEY, supportLangMap } from '@/common';
import classNames from 'classnames';
import { getGlobalCookieJar } from '@/utils/browser/cookie';
import { useTranslation } from '@/modules/ReoI18n';
import Config from '@/config';
import CompDrawerSelect from '../DrawerSelect';
export interface ILangCountryChangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    langList: ISupportLangItem[];
    countryList: ISupportCountryItem[];
}

const CompLangCountryChangeModal: React.FC<ILangCountryChangeModalProps> = (props) => {
    const { t } = useTranslation();

    const onModalClose = props.onClose;
    const currentCountryRef = React.useRef<string>('US');
    const currentLangRef = React.useRef<string>('en');
    const [selectedStateIndex, setSelectedStateIndex] = React.useState(0);
    const [selectedCountry, setSelectCountry] = React.useState(currentCountryRef.current);
    const [selectedLang, setSelectedLang] = React.useState(currentLangRef.current);
    const [showAllCountry, setIsShowAllCountry] = React.useState(false);

    const contentRef = React.useRef<HTMLDivElement>(null);

    const stateSelectOptions = React.useMemo(() => {
        return props.countryList.map((item, index) => {
            return {
                value: index,
                label: item.continentTitle,
            };
        });
    }, [props.countryList]);

    const countrySelectOptions = React.useMemo(() => {
        const countryList = props.countryList[selectedStateIndex]?.countryList;
        if (!countryList) {
            return [];
        }
        return [
            ...countryList,
            {
                value: '_',
                title: t('reolink-header:other-regions'),
                lang: supportLangMap['en'],
                currency: '',
                langKey: 'en',
            },
        ];
    }, [props.countryList, selectedStateIndex, t]);

    const getCurrentState = React.useCallback(() => {
        for (let index = 0; index < props.countryList?.length; index++) {
            const item = props.countryList[index];
            for (const country of item.countryList) {
                if (country.value === currentCountryRef.current) {
                    return index;
                }
            }
        }
        return 0;
    }, [props.countryList]);

    const initModal = React.useCallback(() => {
        const cookieJar = getGlobalCookieJar();
        const currentCountry = cookieJar.getCookie(COUNTRY_COOKIE_KEY) ?? '';
        const currentLang = cookieJar.getCookie(LANGUAGE_COOKIE_KEY) ?? '';
        currentCountryRef.current = currentCountry ? currentCountry : '_';
        currentLangRef.current = currentLang ? langCodeTransform(currentLang) : 'en';
        setSelectedStateIndex(getCurrentState());
        setSelectCountry(currentCountryRef.current);
        setSelectedLang(currentLangRef.current);
    }, [getCurrentState]);

    const handleModalClose = React.useCallback(() => {
        onModalClose();
        /**
         * 关闭弹窗时，清除选中的国家和语言
         */
        initModal();
    }, [initModal, onModalClose]);

    const handleConfirmClick = React.useCallback(() => {
        // 未切换语言直接退出即可
        if (selectedLang === currentLangRef.current && selectedCountry === currentCountryRef.current) {
            handleModalClose();
            return;
        }
        const cookieJar = getGlobalCookieJar();
        if (selectedCountry !== currentCountryRef.current) {
            cookieJar.setCookie(
                COUNTRY_COOKIE_KEY,
                selectedCountry,
                new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
                '/',
                Config.COOKIE_DOMAIN,
            );
        }

        if (selectedLang !== currentLangRef.current) {
            cookieJar.setCookie(
                LANGUAGE_COOKIE_KEY,
                selectedLang,
                new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
                '/',
                Config.COOKIE_DOMAIN,
            );
        }
        else {
            // 只切换了地区，清除语言cookie，使用所选地区的默认语言
            for (const item of countrySelectOptions) {
                if (item.value === selectedCountry) {
                    cookieJar.setCookie(
                        LANGUAGE_COOKIE_KEY,
                        langCodeTransform(item.langKey),
                        new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
                        '/',
                        Config.COOKIE_DOMAIN,
                    );
                }
            }
        }
        location.reload();
    }, [countrySelectOptions, handleModalClose, selectedCountry, selectedLang]);

    React.useEffect(() => {
        if (props.isOpen) {
            initModal();
            document.getElementById(`country-select-item-${currentCountryRef.current}`)?.scrollIntoView({
                behavior: 'instant',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    }, [initModal, props.isOpen]);

    React.useEffect(() => {
        initModal();
    }, [initModal]);

    return (
        <ReoPopup
            open={ props.isOpen }
            onClose={ handleModalClose }
            className={ styles['lang-country-change-modal'] }
            bodyClassName={ styles['lang-country-change-modal-body'] }
            contentClassName={ styles['lang-country-change-modal-content'] }
            closeIcon={ 'icon-co_x' }
            childrenMaxHeight={ 'var(--country_modal_max_height)' }
            closeIconNode={ (
                <div className={ styles['modal-close-button'] }>
                    <ReoIcon
                        onClick={ handleModalClose }
                        className={ styles['modal-close-button-icon'] }
                        name={ 'icon-co_x' }
                    />
                </div>
            ) }
        >
            <div
                className={ styles['modal-body-wrapper'] }
                ref={ contentRef }>
                <div className={ styles['country-lang-change-content'] }>
                    <div className={ styles['country-change'] }>
                        <div className={ styles['country-change-text'] }>
                            <h3 className={ styles['country-change-text-title'] }>
                                {t('reolink-header:choose-country-region-title')}
                            </h3>
                            <p className={ styles['country-change-text-desc'] }>
                                {t('reolink-header:choose-country-region-desc')}
                            </p>
                        </div>
                        <div
                            className={ classNames(styles['country-change-select'], {
                                [styles['show-all-country']]: showAllCountry,
                            }) }
                        >
                            <div className={ styles['state-select'] }>
                                <ReoSelect
                                    options={ stateSelectOptions }
                                    value={ selectedStateIndex }
                                    onChange={ (value) => {
                                        setSelectedStateIndex(value);
                                        setIsShowAllCountry(false);
                                    } }
                                    size={ 'large' }
                                    className={ styles['state-select-select'] }
                                    dropdownClassName={ styles['state-select-dropdown'] }
                                    dropdownAlign={{
                                        overflow: {
                                            adjustX: true,
                                            adjustY: false,
                                        },
                                    }}
                                />
                            </div>
                            <div className={ styles['state-drawer-select'] }>
                                <CompDrawerSelect
                                    options={ stateSelectOptions }
                                    value={ selectedStateIndex }
                                    onChange={ (value) => {
                                        setSelectedStateIndex(value);
                                        setIsShowAllCountry(false);
                                    } }
                                />
                            </div>
                            <div
                                className={ styles['country-select-groups'] }
                                style={
                                    {
                                        '--item_count': countrySelectOptions.length,
                                    } as React.CSSProperties
                                }
                            >
                                {countrySelectOptions?.map((item) => {
                                    return (
                                        <div
                                            className={ classNames(styles['country-select-item'], {
                                                [styles['country-select-item-active']]: selectedCountry === item.value,
                                            }) }
                                            key={ item.value }
                                            onClick={ () => {
                                                setSelectCountry(item.value);
                                            } }
                                            id={ `country-select-item-${item.value}` }
                                        >
                                            <span className={ styles['country-select-item-country'] }>{item.title}</span>
                                            <span className={ styles['country-select-item-lang'] }>
                                                {item.lang}, {item.currency}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            {countrySelectOptions.length > 6 ? (
                                <button
                                    onClick={ () => {
                                        setIsShowAllCountry(!showAllCountry);
                                    } }
                                    className={ styles['show-all-countries-btn'] }
                                >
                                    <span className={ styles['show-all-countries-text'] }>
                                        {showAllCountry ? t('reolink-header:collapse') : t('reolink-header:expand')}
                                    </span>
                                    <ReoIcon
                                        name={ 'icon-co_down' }
                                        className={ styles['show-all-countries-icon'] } />
                                </button>
                            ) : null}
                        </div>
                    </div>
                    <div className={ styles['country-lang-change-divide'] }></div>
                    <div className={ styles['lang-change'] }>
                        <h3 className={ styles['lang-change-title'] }>{t('reolink-header:choose-lang-title')}</h3>
                        <div className={ styles['lang-change-groups'] }>
                            {props.langList.map((item) => {
                                return (
                                    <div
                                        className={ classNames(styles['lang-change-item'], {
                                            [styles['lang-change-item-active']]: selectedLang === item.value,
                                        }) }
                                        key={ item.value }
                                        onClick={ () => {
                                            setSelectedLang(item.value);
                                        } }
                                    >
                                        {item.title}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className={ styles['confirm-button-wrapper'] }>
                    <ReoButton
                        size="large"
                        onClick={ handleConfirmClick }
                        className={ styles['confirm-button'] }>
                        {t('reolink-header:choose-lang-country-save-btn')}
                    </ReoButton>
                </div>
            </div>
        </ReoPopup>
    );
};
export default CompLangCountryChangeModal;
